import BadgeButton from "@/common/components/BadgeButton";
import { experienceTypes } from "@/modules/ExperienceData";
import { eventNames } from "@/modules/Constants";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ExperienceState } from "@/types/state";

import ChecklistIcon from "@svg/checklist.svg";
import HeartIcon from "@svg/heart.svg";
import PlusIcon from "@svg/plus.svg";
import {
  ADD_FAVORITE,
  ADD_FAVORITE_LABEL,
  ANNOTATION,
  ANNOTATION_TEXT,
  ANNOTATION_WRAPPER,
  CONTROLS,
  FOOTER,
  SHOW_FAVORITES,
} from "@/components/shopwindow/productFooterClasses";

const REGION = "Shop Window Favorites Footer";

type ShopWindowFavoritesFooterProps = {
  experienceState: ExperienceState;
  config: ShopWindowConfig;
  pagination?: unknown;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

export default function ShopWindowFavoritesFooter({
  experienceState,
  config,
  pagination: paginationProp,
  onApplicationEvent,
}: ShopWindowFavoritesFooterProps) {
  const experienceData = experienceState.experienceData;
  const clusterIndex = config.clusterArticleIndexAt(
    experienceData,
    config.index,
  );
  const favoritesData = experienceState.favoritesData;
  const article = experienceData.getArticle(
    clusterIndex.clusterName,
    clusterIndex.cindex,
    clusterIndex.index,
  );
  const isSelected = article && favoritesData.isFavorite(article.id);
  const isActive =
    article &&
    article.urls &&
    (article.urls as unknown[]).length &&
    !isSelected;
  const favoriteClass = isSelected ? " selected" : "";
  const hasFavorites = favoritesData.getFavoritesCount() > 0;
  const isChecklist = experienceData.type === experienceTypes.CHECKLIST;

  const Icon = isChecklist ? ChecklistIcon : HeartIcon;
  const iconName = isChecklist ? "checklist" : "heart";
  const DecorationIcon = isSelected ? Icon : PlusIcon;
  const decorationIconName = isSelected ? iconName : "plus";

  const hoverClass = !isSelected && isActive ? " hover" : "";
  const activeClass = isActive ? " active" : " inactive";
  const badgeButtonClass =
    hasFavorites || isChecklist ? " active hover" : " inactive";
  const hasDecoration = false;
  const count = favoritesData.getFavoriteArticles().length;
  const onAddFavorite = isSelected
    ? () => {
        /* no-op */
      }
    : onApplicationEvent;
  const onShowfavorites =
    hasFavorites || isChecklist
      ? onApplicationEvent
      : () => {
          /* no-op */
        };
  const hasBadge =
    count > 0 || experienceData.styleFlags.showFavoritesButtonBadgeZero();

  const spanStyle = {
    marginLeft: hasDecoration ? 35 : 0,
  };

  const decoration = hasDecoration ? (
    <div
      className={`add-favorite-decoration radius-b-left type-2 button square${activeClass}${hoverClass}`}
    >
      <DecorationIcon className={`centered icon-${decorationIconName}-d`} />
    </div>
  ) : (
    ""
  );

  const isHidden = !article;
  const visibility = isHidden ? "hidden" : "visible";
  const pagination = paginationProp ? (
    <div className="swiper-pagination" />
  ) : null;

  const annotation = experienceData.getArticleAnnotation(
    clusterIndex.clusterName,
    clusterIndex.cindex,
    clusterIndex.index,
  );

  return (
    <div
      className={`shop-window-footer tw:w-full ${FOOTER}`}
      style={{ visibility: visibility }}
    >
      <div className={`panel-annotation-wrapper ${ANNOTATION_WRAPPER}`}>
        {pagination}
        <div className={`annotation ${ANNOTATION}`}>
          <span className={`annotation-text ${ANNOTATION_TEXT}`}>
            {annotation}
          </span>
        </div>
      </div>
      <div className={`shop-window-footer-controls ${CONTROLS}`}>
        <button
          className={`add-favorite-btn button type-1 text wide ${ADD_FAVORITE}${activeClass}${favoriteClass}${hoverClass}`}
          onClick={() =>
            onAddFavorite({
              name: eventNames.ADD_FAVORITE,
              region: REGION,
              clusterName: clusterIndex.clusterName,
              cindex: clusterIndex.cindex,
              index: clusterIndex.index,
            })
          }
        >
          {decoration}
          <span className={ADD_FAVORITE_LABEL} style={spanStyle}></span>
        </button>
        <BadgeButton
          className={`show-favorites-btn type-2 ${SHOW_FAVORITES}${badgeButtonClass}`}
          transitionName="show-favorites-btn"
          icon={Icon}
          iconName={iconName}
          value={count}
          hasBadge={hasBadge}
          onClick={() =>
            onShowfavorites({
              name: eventNames.SHOW_FAVORITES,
              region: REGION,
            })
          }
        />
      </div>
    </div>
  );
}
